/**
 * The library as a json-render catalog (https://json-render.dev/docs/catalog),
 * so a generative UI can only build screens from the project's own components.
 * Written by `edl-docs manifest` (and the build) next to llms.txt:
 *
 *   __edl/json-render/catalog.json  components (props as JSON Schema, slots,
 *                                   events, description) and candidates
 *   __edl/json-render/catalog.js    the same wrapped for @json-render/core:
 *                                   `catalog` (defineCatalog) and `candidates`
 *
 * A candidate is one story that is one configured instance of its component:
 * its args become the element's props, and its description is all Jev sees
 * of it when it composes. Stories whose `render` takes no args (grids of
 * variants, compositions) are left out: their args do not draw them.
 *
 * Pure: takes the `session.detail()` of every component.
 */

const SCALAR = ['string', 'number', 'boolean'];
const MULTI = new Set(['check', 'inline-check', 'multi-select']);
const ENUM = new Set(['select', 'radio', 'inline-radio']);

export function jsonRenderCatalog(details, { title = null, renderer = null } = {}) {
  const components = {};
  const candidates = [];
  const skipped = [];
  const taken = new Set();

  for (const d of details) {
    if (!d.stories?.length) continue;
    const props = {};
    const required = [];
    const events = [];
    // A required prop that a story's render fills in itself (Select's options
    // as children) is not required of the candidate built from its args.
    const instances = d.stories.filter((s) => s.args && !s.ignoresArgs);
    const alwaysGiven = (key) => instances.length > 0 && instances.every((s) => s.args[key] !== undefined && !s.args[key]?.__edlUnserialisable);
    // Args hidden from the Controls table are still props the component reads.
    for (const [key, t] of Object.entries(d.argTypes ?? {})) {
      if (isEvent(key, t, d.stories)) {
        events.push(key);
        continue;
      }
      const sample = d.stories.map((s) => s.args?.[key]).find((v) => v !== undefined && !v?.__edlUnserialisable);
      const schema = propSchema(t, sample);
      if (!schema) continue;
      const isRequired = t.required && alwaysGiven(key);
      if (isRequired) required.push(key);
      props[key] = isRequired ? schema : nullable(schema);
      const text = firstParagraph(t.description);
      if (text) props[key].description = text;
    }
    // Args without an argType (Storybook infers none for them) still reach the component.
    for (const s of d.stories) {
      for (const [key, v] of Object.entries(s.args ?? {})) {
        if (key in props || events.includes(key)) continue;
        if (isEvent(key, {}, d.stories)) {
          events.push(key);
          continue;
        }
        const schema = propSchema({}, v);
        if (schema) props[key] = nullable(schema);
      }
    }
    // A slot only where a story passes elements as children through its args,
    // which shows its render puts them on screen. A children type alone is not
    // enough: Axon's AppShell story draws fixed content and drops them, and
    // children passed as text is a label (Button).
    // A story that draws a slot placeholder (Axon's <SbSlotPlaceholder />)
    // shows where content goes; the preview puts the pieces there.
    const passesElements = acceptsChildren(d.argTypes?.children) && instances.some((s) => s.args.children?.__edlUnserialisable === 'React element');
    const slots = renderer === 'react' && (passesElements || instances.some((s) => s.slotPlaceholder)) ? ['default'] : [];
    // Token and foundation pages: nothing to configure, nothing to place.
    if (!Object.keys(props).length && !slots.length && d.stories.every((s) => s.ignoresArgs !== false)) continue;
    const type = typeName(d, taken);

    components[type] = {
      component: d.id,
      ...(d.componentName ? { name: d.componentName } : {}),
      description: componentLine(d),
      props: { type: 'object', properties: props, ...(required.length ? { required } : {}), additionalProperties: false },
      slots,
      events,
    };

    for (const s of d.stories) {
      if (s.ignoresArgs) {
        skipped.push({ id: s.id, reason: 'its render takes no args (a fixed showcase)' });
        continue;
      }
      if (!s.args) {
        skipped.push({ id: s.id, reason: 'args unknown (the story file did not load in the preview)' });
        continue;
      }
      const element = { type, props: {} };
      const dropped = [];
      for (const [key, v] of Object.entries(s.args)) {
        if (v === undefined || events.includes(key)) continue;
        if (!(key in props) || v?.__edlUnserialisable || !fits(props[key], v)) dropped.push(key);
        else element.props[key] = v;
      }
      candidates.push({ id: s.id, description: candidateLine(d, s, element.props), element });
      if (dropped.length) skipped.push({ id: s.id, reason: `kept without ${dropped.join(', ')} (not plain data or outside its options)`, partial: true });
    }
  }

  return { v: 1, title, renderer, components, candidates, skipped };
}

/** catalog.js: catalog.json wrapped for @json-render/core (tested with 0.21.1 and zod 4). */
export function jsonRenderModule(data) {
  const { skipped, ...rest } = data;
  return `// Generated by edl-docs from ${data.title ?? 'the project'}'s stories. Do not edit; run \`edl-docs manifest\`.
// Needs @json-render/core, @json-render/react and zod 4 (z.fromJSONSchema).
import { defineCatalog } from '@json-render/core';
import { schema } from '@json-render/react';
import { z } from 'zod';

export const data = ${JSON.stringify(rest, null, 2)};

export const catalog = defineCatalog(schema, {
  components: Object.fromEntries(
    Object.entries(data.components).map(([type, c]) => [
      type,
      { props: z.fromJSONSchema(c.props), slots: c.slots, events: c.events, description: c.description },
    ]),
  ),
  actions: {},
});

export const candidates = data.candidates;
`;
}

function typeName(d, taken) {
  const segments = d.title.split('/').map((p) => pascal(p)).filter(Boolean);
  const tries = [d.componentName, segments.at(-1), segments.join('')].filter((n) => n && /^[A-Za-z][A-Za-z0-9_]*$/.test(n));
  let name = tries.find((n) => !taken.has(n)) ?? `${tries.at(-1) ?? 'Component'}`;
  for (let i = 2; taken.has(name); i++) name = `${tries.at(-1) ?? 'Component'}${i}`;
  taken.add(name);
  return name;
}

function pascal(text) {
  return text
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('')
    .replace(/^[0-9]+/, '');
}

function isEvent(key, t, stories) {
  if (!/^on[A-Z]/.test(key)) return false;
  if (t.type === 'function' || t.action || /=>|Function|Handler/.test(t.typeSummary ?? '')) return true;
  return !t.control || stories.some((s) => s.args?.[key]?.__edlUnserialisable === 'function');
}

function propSchema(t, sample) {
  const control = t.control?.type ?? null;
  const options = Array.isArray(t.options) ? t.options.filter((o) => SCALAR.includes(typeof o)) : [];
  if (options.length && MULTI.has(control)) return { type: 'array', items: { enum: options } };
  if (options.length && (ENUM.has(control) || !control)) return { enum: options };
  if (control === 'boolean' || t.type === 'boolean') return { type: 'boolean' };
  if (control === 'number' || control === 'range') {
    return { type: 'number', ...(Number.isFinite(t.control.min) ? { minimum: t.control.min } : {}), ...(Number.isFinite(t.control.max) ? { maximum: t.control.max } : {}) };
  }
  if (control === 'text' || control === 'color') return { type: 'string' };
  if (control === 'date') return { type: 'number' };
  if (control === 'object') return {};
  if (sample === undefined || sample === null) return null;
  if (SCALAR.includes(typeof sample)) return { type: typeof sample };
  if (!sample.__edlUnserialisable) return {};
  return null;
}

/** Optional props take null too, as json-render's catalogs expect (`.nullable()`). */
function nullable(schema) {
  if (schema.enum) return { ...schema, enum: [...schema.enum, null] };
  if (typeof schema.type === 'string') return { ...schema, type: [schema.type, 'null'] };
  return schema;
}

function fits(schema, v) {
  if (v === null) return true;
  if (schema.enum) return schema.enum.includes(v);
  if (schema.items?.enum) return Array.isArray(v) && v.every((x) => schema.items.enum.includes(x));
  const types = [].concat(schema.type ?? []);
  if (!types.length) return true;
  return types.includes(Array.isArray(v) ? 'array' : typeof v);
}

function acceptsChildren(t) {
  return Boolean(t) && !t.disabled && /ReactNode|ReactElement|JSX|node|children/i.test(`${t.typeSummary ?? ''} ${t.type ?? ''}`);
}

/** The first paragraph that says what the thing is, skipping labelled notes such as "**Source:** …". */
function firstParagraph(text, max = 200) {
  if (typeof text !== 'string') return null;
  const paragraphs = text.trim().split(/\n\s*\n/).map((x) => x.replace(/\s+/g, ' ').trim());
  const p = paragraphs.find((x) => x && !/^(\*\*|__)[^*_]{1,40}:?(\*\*|__):?/.test(x) && !/^#/.test(x)) ?? '';
  if (!p) return null;
  return p.length > max ? `${p.slice(0, max - 1).trimEnd()}…` : p;
}

function componentLine(d) {
  const what = firstParagraph(d.description, 160);
  return what ? `${d.title.replaceAll('/', ' / ')}. ${what}` : d.title.replaceAll('/', ' / ');
}

/** What Jev reads of a candidate: the component, the story, and the values that make it this one. */
function candidateLine(d, s, props) {
  const values = Object.entries(props)
    .filter(([, v]) => v !== null && v !== '' && v !== false && SCALAR.includes(typeof v))
    .map(([k, v]) => (v === true ? k : typeof v === 'string' && /\s/.test(v) ? `${k} "${v.length > 60 ? `${v.slice(0, 59)}…` : v}"` : `${k} ${v}`));
  const what = firstParagraph(d.description, 120);
  const head = `${d.title.split('/').at(-1).trim()}: ${s.name}`;
  return [head + (values.length ? ` (${values.join(', ')})` : ''), what].filter(Boolean).join('. ');
}

/**
 * Details as `jsonRenderCatalog` takes them, from the preview's `describe()` of
 * each story file: what the Compose page builds in the browser, where the
 * preview already runs, so the dev server needs no headless Chromium.
 */
export function detailsFromDescribed(described) {
  const byId = new Map();
  for (const file of described) {
    if (!file?.stories?.length) continue;
    let d = byId.get(file.componentId);
    if (!d) {
      d = { id: file.componentId, title: file.title, componentName: file.componentName ?? null, description: file.description ?? null, argTypes: {}, stories: [] };
      byId.set(file.componentId, d);
    }
    d.componentName ??= file.componentName ?? null;
    d.description ??= file.description ?? null;
    for (const s of file.stories) {
      for (const [key, t] of Object.entries(s.argTypes ?? {})) if (!(key in d.argTypes)) d.argTypes[key] = t;
      d.stories.push({ id: s.id, name: s.name, description: s.description ?? null, args: s.initialArgs ?? null, ignoresArgs: s.ignoresArgs ?? null, slotPlaceholder: s.slotPlaceholder ?? false });
    }
  }
  return [...byId.values()];
}
